import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CommonTable from "../../../common_components/CommonTable";
import AppButton from "../../../common_components/AppButton";
import PagesCommonHeader from "../../../common_components/PagesCommonHeader";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import TableToolbar from "../../../common_components/TableToolbar";
import ColumnVisibilityPanel from "../../../common_components/ColumnVisibilityPanel";
import { TABLE_TYPES } from "../../../common_components/tableConstants";
import { DEAL_TABLE_OPTIONS } from "../utils";
import { formatDealDate } from "./shared";
// Columns offered in the column-visibility menu (shared across all four deal tables).
const VISIBILITY_COLUMNS = [
    { id: "deal_name", label: "Deal Name" },
    { id: "date", label: "Date" },
    { id: "mrr", label: "MRR" },
    { id: "notes", label: "Notes" },
];
// True when a deal row matches the search text (name / notes / date / MRR).
const matchesSearch = (row, q) => {
    if (!q)
        return true;
    const hay = [row.deal_name, row.notes, formatDealDate(row.date), row.mrr != null ? String(row.mrr) : ""]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return hay.includes(q.toLowerCase());
};
// Currency rendered like the report matrix: a gray "$" then the dark comma-grouped number.
const CurrencyValue = ({ value, bold }) => {
    const safe = value === null || value === undefined || !Number.isFinite(value) ? 0 : value;
    const weight = bold ? 700 : 400;
    return (_jsxs(Typography, { component: "span", className: "all-Font-Sizes", sx: { fontSize: "14px !important", whiteSpace: "nowrap" }, children: [_jsx(Box, { component: "span", sx: { color: "#a4a7ae", fontWeight: weight, mr: 0.4 }, children: "$" }), _jsx(Box, { component: "span", sx: { color: bold ? "#18181b" : "#27272a", fontWeight: weight }, children: Math.round(safe).toLocaleString("en-US") })] }));
};
// The Action (edit/delete) column only shows when the user can manage deals.
const buildColumns = (tableTitle, canManage) => {
    const cols = [
        { id: "deal_name", label: tableTitle, width: canManage ? "30%" : "35%", align: "left", sortable: true },
        { id: "date", label: "DATE", width: "15%", align: "left", sortable: true },
        { id: "mrr", label: "MRR", width: "12%", align: "left", sortable: true },
        { id: "notes", label: "NOTES", width: canManage ? "33%" : "38%", align: "left", sortable: true },
    ];
    if (canManage)
        cols.push({ id: "action", label: "ACTION", width: "10%", align: "center", sortable: false });
    return cols;
};
const sortRows = (rows, sort) => {
    if (!sort?.direction)
        return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        switch (sort.columnId) {
            case "date":
                return (new Date(a.date).getTime() - new Date(b.date).getTime()) * dir;
            case "mrr":
                return ((a.mrr || 0) - (b.mrr || 0)) * dir;
            case "notes":
                return String(a.notes || "").localeCompare(String(b.notes || "")) * dir;
            default:
                return String(a.deal_name || "").localeCompare(String(b.deal_name || "")) * dir;
        }
    });
};
const initialRecord = (value) => ({
    close_to_close: value,
    newly_added: value,
    closed_since_last_report: value,
    lost_from_prior: value,
});
const DealsSection = ({ deals, loading, canManage, onAdd, onEdit, onDelete }) => {
    const [sortConfigs, setSortConfigs] = useState(initialRecord(null));
    const [searchText, setSearchText] = useState("");
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [colVisAnchor, setColVisAnchor] = useState(null);
    const handleSortChange = (type) => (columnId, direction) => {
        setSortConfigs((prev) => ({
            ...prev,
            [type]: direction ? { columnId, direction } : null,
        }));
    };
    const renderCell = (row, column) => {
        switch (column.id) {
            case "deal_name":
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 500, color: "#18181b" }, children: row.deal_name }));
            case "date":
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#27272a", whiteSpace: "nowrap" }, children: formatDealDate(row.date) }));
            case "mrr":
                return row.mrr !== null && row.mrr !== undefined ? (_jsx(CurrencyValue, { value: row.mrr })) : (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#27272a" }, children: "-" }));
            case "notes":
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#52525b" }, children: row.notes || "-" }));
            case "action":
                return (_jsxs(Box, { className: "sr-deal-actions", children: [_jsx(HoverTooltip, { title: "Edit Deal", children: _jsx(IconButton, { size: "small", className: "sr-deal-action-button", onClick: (e) => {
                                    e.stopPropagation();
                                    onEdit(row);
                                }, children: _jsx("img", { src: "/assets/edit.svg", alt: "edit" }) }) }), _jsx(HoverTooltip, { title: "Delete Deal", children: _jsx(IconButton, { size: "small", className: "sr-deal-action-button delete", onClick: (e) => {
                                    e.stopPropagation();
                                    onDelete(row);
                                }, children: _jsx("img", { src: "/assets/delete.svg", alt: "delete" }) }) })] }));
            default:
                return null;
        }
    };
    // Total summary always reflects the FULL table (not just the visible page).
    const renderSummaryRowFor = (allRows) => (_pageRows, column) => {
        if (column.id === "deal_name") {
            return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 700, color: "#18181b" }, children: "Total" }));
        }
        if (column.id === "mrr") {
            const total = allRows.reduce((acc, r) => acc + (Number(r.mrr) || 0), 0);
            // Blank when the total is zero/empty (no "$0", no dash).
            return total > 0 ? _jsx(CurrencyValue, { value: total, bold: true }) : null;
        }
        return null;
    };
    const tables = useMemo(() => DEAL_TABLE_OPTIONS.map((t) => {
        const filtered = (deals[t.value] || []).filter((r) => matchesSearch(r, searchText));
        const sorted = sortRows(filtered, sortConfigs[t.value]);
        return {
            ...t,
            allRows: sorted,
            rows: sorted.map((r) => ({ ...r, id: r._id })),
        };
    }), [deals, sortConfigs, searchText]);
    return (
    // All deal tables grouped inside one white card (Add Deal header on top).
    _jsxs(Paper, { elevation: 0, className: "sr-card sr-deals-card", children: [_jsx(PagesCommonHeader, { height: "48px", rightSection: _jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsx(TableToolbar, { title: "", showSearch: true, showFullScreen: true, searchValue: searchText, onSearchChange: setSearchText, searchPlaceholder: "Search deals...", enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColVisAnchor(e.currentTarget) }), canManage && (_jsx(AppButton, { variant: "contained", color: "primary", size: "small", startIcon: _jsx(AddIcon, { sx: { fontSize: "16px !important" } }), onClick: onAdd, sx: { height: "32px", fontSize: "13px", fontWeight: 500, textTransform: "none", px: 2, whiteSpace: "nowrap" }, children: "Add Deal" }))] }) }), _jsx(ColumnVisibilityPanel, { open: Boolean(colVisAnchor), anchorEl: colVisAnchor, onClose: () => setColVisAnchor(null), columns: VISIBILITY_COLUMNS, hiddenColumns: hiddenColumns, onVisibilityChange: setHiddenColumns }), _jsx(Box, { className: "sr-deals-body", children: tables.map((table) => (_jsxs(Box, { className: "sr-deal-table-group", children: [_jsx(Box, { className: "sr-table-wrapper", children: _jsx(Box, { className: "sr-table-box", children: _jsx(CommonTable, { columns: buildColumns(table.tableTitle, canManage), rows: table.rows, renderCell: renderCell, type: TABLE_TYPES.GROUPED, tableHeadClassName: "reusable-table-header-label all-Font-Sizes", isLoading: loading, emptyMessage: "No data available in table", hiddenColumns: hiddenColumns, sortConfig: sortConfigs[table.value], onSortChange: handleSortChange(table.value), enableSummaryRow: true, renderSummaryRow: renderSummaryRowFor(table.allRows), summaryRowPosition: "bottom", expandTrigger: "none", stickyHeader: true, maxHeight: "485px" }) }) }), _jsx(Box, { className: "sr-pagination-wrapper", children: _jsx(Typography, { className: "sr-entries-text all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 500 }, children: table.allRows.length > 0
                                    ? `Showing 1 to ${table.allRows.length} of ${table.allRows.length} entries`
                                    : "Showing 0 to 0 of 0 entries" }) })] }, table.value))) })] }));
};
export default DealsSection;
